<template>
  <v-dialog v-model="show" max-width="600">
    <v-sheet class="border-opacity-25 border-thin rounded-lg pa-5">
      <v-card>
        <v-form ref="form" @submit.prevent="onSubmit">
          <v-card-title class="headline">{{ buttonTxt }}</v-card-title>
          <v-card-text>
            <v-text-field
              v-model="field.name"
              label="Field Name"
              :rules="[rules.required]"></v-text-field>
            <v-text-field
              v-model="field.description"
              label="Field Description"
              :rules="[rules.required]"></v-text-field>
            <v-select
              v-model="field.type"
              :items="fieldTypeOptions"
              item-title="label"
              item-value="value"
              label="Field Type"
              :rules="[rules.required]"></v-select>
            <div v-if="field.hasOptions()">
              <div class="justify-between d-flex align-center mb-2">
                <h4 class="d-flex">Options</h4>
                <div class="d-flex justify-end mb-2">
                  <v-icon color="primary" @click="addNewOption">mdi-plus</v-icon>
                </div>
              </div>
              <draggable
                v-model="options"
                item-key="order"
                :group="{ name: 'entry-options', pull: 'clone', put: false }"
                :animation="200"
                :ghostClass="'ghost'"
                :forceFallback="true"
                handle=".drag-handle"
                @end="onDragEnd">
                <template #item="{ element }">
                  <v-row>
                    <v-col cols="12" md="1" class="d-flex align-center justify-center">
                      <v-icon class="cursor-move-handle drag-handle">mdi-drag</v-icon>
                    </v-col>

                    <v-col cols="12" md="10" class="ignore-drag">
                      <v-text-field
                        class="ignore-drag"
                        v-model="element.name"
                        :rules="[rules.required]"></v-text-field>
                    </v-col>
                    <v-col
                      v-if="options.length > 1"
                      cols="12"
                      md="1"
                      class="d-flex align-center justify-center ignore-drag">
                      <v-icon @click="removeOption(element)">mdi-trash-can-outline</v-icon>
                    </v-col>
                  </v-row>
                </template>
              </draggable>
            </div>
            <v-divider></v-divider>
            <v-checkbox v-model="field.is_required" label="Is Required"></v-checkbox>
          </v-card-text>
          <v-card-actions>
            <v-row>
              <v-col>
                <v-btn
                  color="secondary"
                  block
                  class="rounded-lg mb-8 mt-8"
                  elevation="2"
                  @click="emit('entryFieldCancled')"
                  >Cancel</v-btn
                ></v-col
              >
              <v-col>
                <v-btn
                  type="submit"
                  color="primary"
                  block
                  class="rounded-lg mb-8 mt-8"
                  elevation="2"
                  >{{ buttonTxt }}</v-btn
                >
              </v-col>
            </v-row>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-sheet>
  </v-dialog>
</template>
<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { SortableEvent } from 'sortablejs';
import draggable from 'vuedraggable';
import type { VForm } from 'vuetify/components';
import {
  EntryFieldDTO,
  EntrySectionDTO,
  EntryOptionDTO,
  FieldTypeEnum,
  IEntryFieldDTO,
  IEntryOptionDTO,
  IEntrySectionDTO,
} from '@/dtos';
import { useEntryStore, useUpdateStatusStore } from '@/stores';
import { SNACKSTATUS } from '@/stores/util';
import { rules } from '@/helpers';

const props = defineProps<{
  section: IEntrySectionDTO;
  isOpen: boolean;
  isAdd: boolean;
  yearId: string;
  field?: IEntryFieldDTO;
  entryId: string;
}>();
const section = computed(() => props.section);
const yearId = computed(() => props.yearId);
const entryId = computed(() => props.entryId);
const emit = defineEmits(['snack', 'entryFieldCreated', 'entryFieldCancled']);
const show = computed(() => props.isOpen);
const isAdd = computed(() => props.isAdd);
const form = ref<InstanceType<typeof VForm> | null>();
const field = ref<IEntryFieldDTO>(new EntryFieldDTO());
const entryStore = useEntryStore();
const statusStore = useUpdateStatusStore();
const buttonTxt = computed(() => (isAdd.value ? 'Add Field' : 'Update Field'));
const options = ref<IEntryOptionDTO[]>([]);
watch(
  () => props.field,
  () => {
    if (props.field) {
      field.value = new EntryFieldDTO(props.field);
    } else {
      field.value = new EntryFieldDTO();
    }
  },
  { immediate: true },
);

watch(
  () => [field.value.hasOptions(), field.value.options],
  () => {
    if (field.value.hasOptions() && field.value.options.length == 0) {
      options.value.push(new EntryOptionDTO());
    } else {
      options.value = field.value.options.sort((a, b) => a.order - b.order);
    }
  },
  { immediate: true },
);

const fieldTypeOptions = computed(() =>
  Object.values(FieldTypeEnum).map((value) => ({
    label: value
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase()),
    value,
  })),
);

const onSubmit = async () => {
  if (!form.value) {
    return;
  }
  const { valid } = await form.value.validate();
  if (!valid) return;
  if (!field.value.section || !field.value.section.id) {
    field.value.section = new EntrySectionDTO();
    field.value.section.id = section.value.id;
  }

  if (field.value.hasOptions()) {
    field.value.options = options.value;
  }
  if (isAdd.value) {
    entryStore.createField.mutateAsync({ field: field.value, yearId: yearId.value });
  } else {
    entryStore.updateField.mutateAsync({
      entryId: entryId.value,
      sectionId: section.value.id,
      field: field.value,
      yearId: yearId.value,
    });
  }
};

watch(
  () => statusStore.getStatus(`entry-field-create`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `entry-field-create`;
    emit('snack', 'Field', value, statusStore.getError(key));
    emit('entryFieldCreated');
    field.value = new EntryFieldDTO();
  },
);

const key = computed(() => `entry-field-update-${field.value.id}`);
watch(
  () => statusStore.getStatus(key.value),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    emit('snack', 'Field', value, statusStore.getError(key.value));
    emit('entryFieldCreated');
  },
);

const removeOption = (option: IEntryOptionDTO) => {
  const index = options.value.findIndex((o) => o.id === option.id);
  if (index !== -1) {
    options.value.splice(index, 1);
  }
};

function onDragEnd(evt: SortableEvent) {
  if (evt.oldIndex === evt.newIndex) {
    return;
  }
  options.value.forEach((option, index) => {
    option.order = index + 1;
  });
}

function addNewOption() {
  let newOption = new EntryOptionDTO();
  newOption.order = options.value.length + 1;
  options.value.push(newOption);
}
</script>
